import { router } from 'expo-router';
import {
    Animations,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulCustomIcon,
    FullScreenSpinner,
    H3Emphasized,
    H5Emphasized,
    ScreenContainer,
    Shadows,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { DummyFruitfulCard } from '../../../../banking/components/cards/FruitfulCard';
import { CardColor, CardForm } from '../../../../banking/types/Card';
import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { getPartnerDisplayFirstName } from '../../../../common/utils/userUtils';
import { useViewEvents } from '../../../../events/hooks/useViewEvents';
import { useMoneyMapWorkflow } from '../../../hooks/useMoneyMapWorkflow';
import {
    type ApexAccountsStepCompleteness,
    type CardAccountsStepCompleteness,
    type CashAccountsStepCompleteness,
    GoWorkflowStepType,
} from '../../../types/GoWorkflow';
import { GoWorkflowHeader } from '../../GoWorkflowHeader';

const IconTile = ({
    color,
    useAltColors = false,
}: {
    color: string;
    useAltColors?: boolean;
}) => {
    return (
        <FruitfulCustomIcon
            icon="fruitful"
            size="lg"
            showShadow={true}
            color={color}
            useAltColors={useAltColors}
        />
    );
};

const CashOnlySuccessGraphic = () => {
    return (
        <YStack alignItems="center" justifyContent="center" marginBottom={30}>
            <View marginLeft={25} transform={[{ rotate: '7deg' }]}>
                <IconTile color="red" />
            </View>
            <XStack gap={100}>
                <View marginTop={-10} transform={[{ rotate: '-24deg' }]}>
                    <IconTile color="blue" />
                </View>
                <View marginTop={10} transform={[{ rotate: '27deg' }]}>
                    <IconTile color="peach" />
                </View>
            </XStack>
            <View
                marginRight={25}
                marginTop={-5}
                transform={[{ rotate: '-6deg' }]}
            >
                <IconTile color="green" />
            </View>
        </YStack>
    );
};

const CashAndCardSuccessGraphic = () => {
    return (
        <YStack alignItems="center" justifyContent="center">
            <XStack justifyContent="center">
                <View marginTop={27} marginRight={-10} rotate="6.8deg">
                    <IconTile color="blue" />
                </View>
                <View zIndex={1} rotate="-1.9deg">
                    <IconTile color="peach" />
                </View>
                <View marginTop={28} marginLeft={-11} rotate="-7.1deg">
                    <IconTile color="green" />
                </View>
            </XStack>
            <YStack
                alignItems="center"
                marginTop={20}
                transformOrigin="center top"
                scale={0.43}
                maxHeight={140}
            >
                <View transform={[{ translateX: 40 }]} zIndex={1}>
                    <DummyFruitfulCard
                        form={CardForm.PHYSICAL}
                        showFormLabel={false}
                        style={{ ...Shadows.LARGE }}
                    />
                </View>
                <View
                    transform={[
                        { translateX: -30 },
                        { translateY: -60 },
                        { rotate: '-12.3deg' },
                    ]}
                >
                    <DummyFruitfulCard
                        form={CardForm.VIRTUAL}
                        color={CardColor.GREEN}
                        showFormLabel={false}
                        style={{ ...Shadows.LARGE }}
                    />
                </View>
            </YStack>
        </YStack>
    );
};

const ApexAccountsSuccessGraphic = () => {
    return (
        <YStack alignItems="center" justifyContent="center" marginBottom={30}>
            <View marginLeft={25} transform={[{ rotate: '8deg' }]}>
                <IconTile color="green" useAltColors={true} />
            </View>
            <XStack gap={15} marginTop={-10}>
                <View transform={[{ rotate: '-18deg' }]}>
                    <IconTile color="blue" useAltColors={true} />
                </View>
                <View marginTop={10} transform={[{ rotate: '12deg' }]}>
                    <IconTile color="red" useAltColors={true} />
                </View>
            </XStack>
        </YStack>
    );
};

const getTitle = (
    isAccountInvitationsOnly: boolean,
    isCashAndCardAccounts: boolean,
    isApexAccounts: boolean,
    isSingleAccountAction: boolean,
    awaitingPartnerActions: boolean,
) => {
    if (isAccountInvitationsOnly) {
        return isApexAccounts
            ? isSingleAccountAction
                ? 'You have joined your Fruitful Investment Account!'
                : 'You have joined your Fruitful Investment Accounts!'
            : isCashAndCardAccounts
              ? 'You have joined your Fruitful Cash and Card Accounts!'
              : isSingleAccountAction
                ? `You have joined your Fruitful Cash Account!`
                : `You have joined your Fruitful Cash Accounts!`;
    }

    if (isApexAccounts) {
        // TODO need to handle other possible states that would lead to "Your application is in progress"
        // e.g. based on result of IPQ / background, and/or whether related cash accounts are fully created?
        return awaitingPartnerActions
            ? isSingleAccountAction
                ? 'Your Fruitful Investment Account is partially open'
                : 'Your Fruitful Investment Accounts are partially open'
            : isSingleAccountAction
              ? 'Your Fruitful Investment Account is open!'
              : 'Your Fruitful Investment Accounts are open!';
    }

    return isCashAndCardAccounts
        ? `Your Fruitful Cash and Card Accounts are open!`
        : isSingleAccountAction
          ? `Your Fruitful Cash Account is open!`
          : `Your Fruitful Cash Accounts are open!`;
};

const getSubtitle = (
    isNextStepApexAccounts: boolean,
    isNextStepIncomeSplit: boolean,
    isSingleAccountAction: boolean,
    isNextStepCmmIm: boolean,
    awaitingPartnerActions: boolean,
) => {
    if (isNextStepCmmIm) {
        return '';
    }

    if (awaitingPartnerActions) {
        return '';
    }

    return isNextStepApexAccounts
        ? "Now, let's review and open your Fruitful Investment Accounts."
        : isNextStepIncomeSplit
          ? "Now, let's confirm your Income Split rules."
          : isSingleAccountAction
            ? "Now, let's fund your account."
            : "Now, let's fund your accounts.";
};

const getAwaitingPartnerActionsMessage = (
    cashAccountsStep: CashAccountsStepCompleteness,
    cardAccountsStep: CardAccountsStepCompleteness,
    apexAccountsStep: ApexAccountsStepCompleteness | undefined,
    isNextStepIncomeSplit: boolean,
    partnerName: string,
) => {
    const countPartnerAccountsToOpen =
        cashAccountsStep.partnerSoloAccountsToOpen.length +
        cardAccountsStep.partnerSoloAccountsToOpen.length +
        (apexAccountsStep?.partnerSoloAccountsToOpen.length ?? 0);
    const countPartnerAccountsToAccept =
        cashAccountsStep.partnerJointAccountsToAccept.length +
        cardAccountsStep.partnerJointAccountsToAccept.length +
        (apexAccountsStep?.partnerJointAccountsToAccept.length ?? 0);

    if (countPartnerAccountsToOpen > 0) {
        const accountsText =
            countPartnerAccountsToOpen === 1 ? 'account' : 'accounts';
        if (isNextStepIncomeSplit) {
            return `Waiting for individual ${accountsText} to be opened by ${partnerName}, and then you can complete your Money Map.`;
        } else {
            return `Now ${partnerName} can log in to open the remaining individual ${accountsText} from your Money Map.`;
        }
    }

    if (countPartnerAccountsToAccept > 0) {
        const accountsText =
            countPartnerAccountsToAccept === 1 ? 'account' : 'accounts';
        if (isNextStepIncomeSplit) {
            return `We just need ${partnerName} to join your joint ${accountsText}, and then you can complete your Money Map.`;
        } else {
            return `Now ${partnerName} can log in to join the joint ${accountsText} from your Money Map.`;
        }
    }

    return null;
};

export const AccountsStepSuccess = ({
    currentStepType,
    isAccountInvitationsOnly,
    isSingleAccountAction = false,
}: {
    currentStepType: GoWorkflowStepType;
    isAccountInvitationsOnly: boolean;
    isSingleAccountAction?: boolean;
}) => {
    const { trackViewOnce } = useViewEvents();

    const user = useAuthedUser();
    const {
        isLoadingCompleteness,
        moneyMapCompleteness,
        getNextStep,
        proceedForward,
    } = useMoneyMapWorkflow();
    const { cashAccountsStep, cardAccountsStep, apexAccountsStep } =
        moneyMapCompleteness;
    const isCashAndCardAccounts =
        currentStepType === GoWorkflowStepType.CARD_ACCOUNTS;
    const isApexAccounts = currentStepType === GoWorkflowStepType.APEX_ACCOUNTS;
    const nextStep = getNextStep(currentStepType);
    const isNextStepApexAccounts =
        nextStep?.type === GoWorkflowStepType.APEX_ACCOUNTS;
    const isNextStepCmmIm = nextStep?.type === GoWorkflowStepType.CMM_IM;
    const isNextStepIncomeSplit = isNextStepCmmIm
        ? true
        : nextStep?.type === GoWorkflowStepType.INCOME_SPLIT;

    // We don't want to handle any 'awaiting partner' state if the next step is Investment Accounts
    const awaitingPartnerActions = isNextStepApexAccounts
        ? false
        : (cashAccountsStep.isIncluded &&
              !cashAccountsStep.isCompleteForMembership) ||
          (cardAccountsStep.isIncluded &&
              !cardAccountsStep.isCompleteForMembership) ||
          ((apexAccountsStep?.isIncluded &&
              !apexAccountsStep?.isCompleteForMembership) ??
              false);

    const incomeSplitBlockedAwaitingPartnerActions =
        awaitingPartnerActions && isNextStepIncomeSplit;

    trackViewOnce(
        incomeSplitBlockedAwaitingPartnerActions
            ? 'Roadmap/Go/AccountsStepSuccess_AwaitingPartner'
            : isNextStepApexAccounts
              ? 'Roadmap/Go/AccountsStepSuccess_InvestmentAccounts'
              : isNextStepIncomeSplit
                ? 'Roadmap/Go/AccountsStepSuccess_IncomeSplit'
                : 'Roadmap/Go/AccountsStepSuccess_TransferFunds',
    );

    if (isLoadingCompleteness) {
        return <FullScreenSpinner />;
    }

    const partnerName = getPartnerDisplayFirstName(
        user.partnerMember?.firstName,
        'your partner',
    );
    const partnerEmail = user.partnerMember?.email ?? null;

    const title = getTitle(
        isAccountInvitationsOnly,
        isCashAndCardAccounts,
        isApexAccounts,
        isSingleAccountAction,
        awaitingPartnerActions,
    );

    const subtitle = getSubtitle(
        isNextStepApexAccounts,
        isNextStepIncomeSplit,
        isSingleAccountAction,
        isNextStepCmmIm,
        awaitingPartnerActions,
    );

    const awaitingPartnerActionsMessage = getAwaitingPartnerActionsMessage(
        cashAccountsStep,
        cardAccountsStep,
        apexAccountsStep,
        isNextStepIncomeSplit,
        partnerName,
    );

    return (
        <View flex={1}>
            <GoWorkflowHeader
                hasMessagingButton={false}
                onBack={() => {
                    router.navigate('/roadmap/go/money-map');
                }}
            />
            <ScreenContainer>
                <YStack justifyContent="space-between">
                    <YStack
                        alignItems="center"
                        justifyContent="center"
                        {...Animations.FadeInFromBelow}
                        paddingBottom={24}
                    >
                        {isApexAccounts ? (
                            <ApexAccountsSuccessGraphic />
                        ) : isCashAndCardAccounts ? (
                            <CashAndCardSuccessGraphic />
                        ) : (
                            <CashOnlySuccessGraphic />
                        )}
                        <YStack gap={8} $sm={{ marginTop: 16 }}>
                            <H3Emphasized textAlign="center">
                                {title}
                            </H3Emphasized>
                            <BodyMediumRegular
                                textAlign="center"
                                color="$gray1100"
                            >
                                {subtitle}
                            </BodyMediumRegular>
                        </YStack>
                    </YStack>
                    <YStack width="100%" gap={16} {...Animations.FadeIn}>
                        {awaitingPartnerActions &&
                            awaitingPartnerActionsMessage != null && (
                                <YStack
                                    marginTop={16}
                                    paddingVertical={20}
                                    paddingHorizontal={16}
                                    borderRadius={16}
                                    backgroundColor="$gray200"
                                    alignItems="center"
                                    gap={8}
                                    maxWidth={500}
                                    alignSelf="center"
                                >
                                    <H5Emphasized>
                                        You&apos;re almost there!
                                    </H5Emphasized>
                                    <BodyMediumRegular
                                        color="$gray1100"
                                        textAlign="center"
                                    >
                                        {awaitingPartnerActionsMessage}
                                    </BodyMediumRegular>
                                    {partnerEmail != null && (
                                        <BodyMediumRegular
                                            color="$gray1100"
                                            textAlign="center"
                                        >
                                            We&apos;ve sent {partnerName} a
                                            message at {partnerEmail}.
                                        </BodyMediumRegular>
                                    )}
                                </YStack>
                            )}
                        {isNextStepApexAccounts ? (
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.LARGE}
                                text="Open Investment Accounts"
                                onPress={() => {
                                    proceedForward(currentStepType);
                                }}
                            />
                        ) : incomeSplitBlockedAwaitingPartnerActions ? (
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.LARGE}
                                text="Dismiss"
                                onPress={() => {
                                    router.navigate('/home/roadmap');
                                }}
                            />
                        ) : isNextStepIncomeSplit ? (
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.LARGE}
                                text={
                                    isNextStepCmmIm
                                        ? 'Continue'
                                        : 'Review Income Split rules'
                                }
                                onPress={() => {
                                    proceedForward(currentStepType);
                                }}
                            />
                        ) : (
                            <>
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    text="Transfer funds"
                                    onPress={() => {
                                        router.push('/banking/transfer');
                                    }}
                                />
                                <Button
                                    variant={ButtonVariant.TERTIARY}
                                    size={ButtonSize.SMALL}
                                    text={`I'll fund my ${isSingleAccountAction ? 'account' : 'accounts'} later`}
                                    onPress={() => {
                                        router.push('/home/roadmap');
                                    }}
                                />
                            </>
                        )}
                    </YStack>
                </YStack>
            </ScreenContainer>
        </View>
    );
};
